import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import BottomNav from '../BottomNav';
import ApplicationCard from './ApplicationCard';
import CompletionCard from './CompletionCard';
import CancellationCard from './CancellationCard';
import ReceiptCard from './ReceiptCard';
import OfferCard from './OfferCard';
import MediaMessage from './MediaMessage';
import { sendPushNotification } from '../sendPushNotification';
import { useTrackEvent } from '../hooks/useAnalytics';
import theme from '../theme';

function BadgeVerificato({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: 4, flexShrink: 0 }}>
      <path d="M8 1L2 3.5V8c0 3.3 2.5 5.8 6 7 3.5-1.2 6-3.7 6-7V3.5L8 1z" fill={theme.colors.white}/>
      <path d="M5.5 8l2 2 3-3" stroke={theme.colors.primary} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function ChatDetail({ session, profile }) {
  const { conversationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();
  const trackedChatRef = useRef(null);
  const { otherPerson, jobTitle, avatarUrl, isVerified } = location.state || {};

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [application, setApplication] = useState(null);
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);
  const [otherPersonId, setOtherPersonId] = useState(null);
  const [applicantId, setApplicantId] = useState(null);
  const [employerId, setEmployerId] = useState(null);
  const [isApplicant, setIsApplicant] = useState(false);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [offerPrice, setOfferPrice] = useState('');
  const [offerPriceType, setOfferPriceType] = useState('fisso');
  const [offerHours, setOfferHours] = useState('');
  const [sendingOffer, setSendingOffer] = useState(false);
  const [completionCardVisible, setCompletionCardVisible] = useState(true);
  const [showMediaMenu, setShowMediaMenu] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);

  const bottomRef = useRef(null);
  const completionCardRef = useRef(null);
  const observerRef = useRef(null);
  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  const applicationId = conversationId?.replace('app_', '');
  const senderDisplayName = profile?.full_name || session?.user?.email;

  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (mediaRecorderRef.current && recording) {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  useEffect(() => {
    fetchMessages();
    fetchApplication();
    markAsRead();

    // Traccia "chat_visualizzata" all'apertura di questa conversazione,
    // una sola volta per conversationId (evita doppio log da StrictMode/re-render)
    if (trackedChatRef.current !== conversationId) {
      trackEvent('chat_visualizzata', {
        conversation_id: conversationId,
        application_id: applicationId,
      });
      trackedChatRef.current = conversationId;
    }

    const channel = supabase
      .channel(`chat-${conversationId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload) => {
        if (payload.new.conversation_id === conversationId) {
          fetchMessages();
          markAsRead();
          scrollToBottom();
        }
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'applications' }, (payload) => {
        if (payload.new.id === parseInt(applicationId)) {
          setApplication(prev => ({ ...prev, ...payload.new }));
        }
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [conversationId]);

  useEffect(() => { scrollToBottom(); }, [messages]);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(
      ([entry]) => { setCompletionCardVisible(entry.isIntersecting); },
      { threshold: 0.1 }
    );
    if (completionCardRef.current) observerRef.current.observe(completionCardRef.current);
    return () => observerRef.current?.disconnect();
  }, [messages]);

  const fetchApplication = async () => {
    const { data: app } = await supabase
      .from('applications').select('*, jobs(*)')
      .eq('id', applicationId).single();

    if (app) {
      setApplication(app);
      const applicant = app.applicant_email === session.user.email;
      setIsApplicant(applicant);

      if (app.completed) {
        const { data: review } = await supabase
          .from('reviews').select('id')
          .eq('application_id', applicationId)
          .eq('reviewer_email', session.user.email).single();
        if (review) setAlreadyReviewed(true);
      }

      // Committente (autore annuncio) — sempre determinato da jobs.author, fisso indipendentemente da chi visualizza
      let employerProfileId = null;
      const { data: authorProfile } = await supabase
        .from('profiles').select('id').eq('full_name', app.jobs?.author).single();
      if (authorProfile) {
        employerProfileId = authorProfile.id;
        setEmployerId(authorProfile.id);
      }

      // Candidato (lavoratore) — sempre determinato dall'email della candidatura, fisso indipendentemente da chi visualizza
      let applicantProfileId = null;
      const { data: applicantEmailProfile } = await supabase.rpc('get_profile_by_email', { user_email: app.applicant_email });
      if (applicantEmailProfile && applicantEmailProfile.length > 0) {
        const { data: applicantProfileRow } = await supabase
          .from('profiles').select('id').eq('full_name', applicantEmailProfile[0].full_name).single();
        if (applicantProfileRow) {
          applicantProfileId = applicantProfileRow.id;
          setApplicantId(applicantProfileRow.id);
        }
      }

      setOtherPersonId(applicant ? employerProfileId : applicantProfileId);
    }
  };

  const fetchMessages = async () => {
    const { data } = await supabase
      .from('messages').select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });
    if (data) setMessages(data);
  };

  const markAsRead = async () => {
    await supabase.from('messages').update({ read: true })
      .eq('conversation_id', conversationId)
      .neq('sender_id', session.user.id);
  };

  const scrollToBottom = () => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const scrollToCompletionCard = () => {
    completionCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    const textToSend = newMessage.trim();
    const { error } = await supabase.from('messages').insert([{
      conversation_id: conversationId,
      sender_id: session.user.id,
      content: textToSend,
      read: false, type: 'text',
    }]);
    if (!error) {
      setNewMessage('');
      if (otherPersonId) {
        sendPushNotification({
          userId: otherPersonId,
          title: senderDisplayName,
          body: textToSend,
          url: `/chat/${conversationId}`,
        });
      }
    }
    setSending(false);
  };

  const handleSendOffer = async () => {
    if (!offerPrice) { alert('Inserisci un prezzo!'); return; }
    if (offerPriceType === 'ora' && !offerHours) { alert('Inserisci il numero di ore concordate!'); return; }
    setSendingOffer(true);

    const isOra = offerPriceType === 'ora';
    const total = isOra ? (parseFloat(offerPrice) * parseFloat(offerHours)).toFixed(2) : null;
    const priceLabel = isOra ? `${offerPrice}/h × ${offerHours}h = €${total}` : offerPrice;

    await supabase.from('messages').insert([{
      conversation_id: conversationId,
      sender_id: session.user.id,
      content: `💼 Offerta formale — €${priceLabel}`,
      read: false, type: 'offer',
      offer_data: {
        price: offerPrice, price_type: offerPriceType,
        hours: isOra ? offerHours : null,
        dates: application?.selected_dates || [],
        time_slot: application?.jobs?.time_slot || null,
      },
    }]);

    // Traccia "offerta_inviata" — copre sia offerte formali iniziali che rilanci/controproposte
    trackEvent('offerta_inviata', {
      conversation_id: conversationId,
      application_id: applicationId,
      price: offerPrice,
      price_type: offerPriceType,
      hours: isOra ? offerHours : null,
    });

    if (otherPersonId) {
      sendPushNotification({
        userId: otherPersonId,
        title: senderDisplayName,
        body: `💼 Offerta formale — €${priceLabel}`,
        url: `/chat/${conversationId}`,
      });
    }
    setOfferPrice(''); setOfferPriceType('fisso'); setOfferHours('');
    setShowOfferForm(false); setSendingOffer(false);
  };

  const handleFileUpload = async (file, mediaType) => {
    if (!file) return;
    setUploadingMedia(true);
    setShowMediaMenu(false);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `${session.user.id}_${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('chat-media').upload(fileName, file, { upsert: true });
      if (uploadError) { alert('Errore upload: ' + uploadError.message); return; }
      const { data: urlData } = supabase.storage.from('chat-media').getPublicUrl(fileName);
      const previewText = mediaType === 'document' ? `📄 ${file.name}` : mediaType === 'video' ? '🎥 Video' : '📷 Foto';
      await supabase.from('messages').insert([{
        conversation_id: conversationId,
        sender_id: session.user.id,
        content: previewText,
        read: false, type: 'media',
        offer_data: { url: urlData.publicUrl, media_type: mediaType, file_name: file.name },
      }]);
      if (otherPersonId) {
        sendPushNotification({
          userId: otherPersonId,
          title: senderDisplayName,
          body: previewText,
          url: `/chat/${conversationId}`,
        });
      }
    } finally { setUploadingMedia(false); }
  };

  const getSupportedMimeType = () => {
    const types = ['audio/webm', 'audio/mp4', 'audio/ogg'];
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) return type;
    }
    return '';
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedMimeType();
      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });
        setUploadingMedia(true);
        try {
          const ext = mimeType === 'audio/mp4' ? 'm4a' : mimeType === 'audio/ogg' ? 'ogg' : 'webm';
          const fileName = `audio_${session.user.id}_${Date.now()}.${ext}`;
          const { error } = await supabase.storage
            .from('chat-media').upload(fileName, blob, { upsert: true, contentType: mimeType || 'audio/webm' });
          if (error) { alert('Errore upload audio'); return; }
          const { data: urlData } = supabase.storage.from('chat-media').getPublicUrl(fileName);
          await supabase.from('messages').insert([{
            conversation_id: conversationId,
            sender_id: session.user.id,
            content: '🎤 Messaggio vocale',
            read: false, type: 'media',
            offer_data: { url: urlData.publicUrl, media_type: 'audio', file_name: fileName },
          }]);
          if (otherPersonId) {
            sendPushNotification({
              userId: otherPersonId,
              title: senderDisplayName,
              body: '🎤 Messaggio vocale',
              url: `/chat/${conversationId}`,
            });
          }
        } finally { setUploadingMedia(false); }
      };

      mediaRecorder.start();
      setRecording(true);
      setRecordingSeconds(0);
      recordingTimerRef.current = setInterval(() => setRecordingSeconds(s => s + 1), 1000);
    } catch (err) {
      alert('Impossibile accedere al microfono: ' + err.message);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      clearInterval(recordingTimerRef.current);
      setRecording(false);
      setRecordingSeconds(0);
    }
  };

  const formatSec = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const formatTime = (dateStr) => new Date(dateStr).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Oggi';
    if (d.toDateString() === yesterday.toDateString()) return 'Ieri';
    return d.toLocaleDateString('it-IT');
  };

  const formatOfferTotal = (offerData) => {
    if (!offerData) return '';
    if (offerData.price_type === 'ora' && offerData.hours) {
      const total = (parseFloat(offerData.price) * parseFloat(offerData.hours)).toFixed(2);
      return `€${offerData.price}/h × ${offerData.hours}h = €${total}`;
    }
    return offerData.price_type === 'ora' ? `€${offerData.price}/h` : `€${offerData.price}`;
  };

  const groupedMessages = messages.reduce((groups, msg) => {
    const date = formatDate(msg.created_at);
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
    return groups;
  }, {});

  const completionMsg = [...messages].reverse().find(m => m.type === 'completion_request');
  const completionMsgId = completionMsg?.id;
  const isCancelled = application?.status === 'cancellata';
  const alreadyCompleted = application?.completed && !isCancelled;
  const iConfirmed = isApplicant ? application?.confirmed_by_worker : application?.confirmed_by_employer;
  const hasCompletionRequest = !!completionMsg;
  const hasPendingCancellation = application?.cancellation_status === 'richiesta_committente';
  const showOfferButton = !alreadyCompleted && !isCancelled && application?.status !== 'accettata' && (isApplicant || application?.status === 'controproposta');
  const showStickyBannerPending = hasCompletionRequest && !completionCardVisible && !alreadyCompleted && !isCancelled && !iConfirmed && !hasPendingCancellation;
  const showStickyBannerConfirmed = hasCompletionRequest && !completionCardVisible && !alreadyCompleted && !isCancelled && iConfirmed && !hasPendingCancellation;
  const showTopReviewBanner = alreadyCompleted && !alreadyReviewed;
  const showCancellationCard = application && (
    application.status === 'accettata' ||
    (isCancelled && application.cancellation_status)
  );
  return (
    <div style={{ maxWidth: theme.layout.maxWidth, margin: '0 auto', fontFamily: theme.font.family, display: 'flex', flexDirection: 'column', height: '100vh' }}>

      <input ref={fileInputRef} type="file" accept="image/*,video/*" style={{ display: 'none' }}
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) handleFileUpload(file, file.type.startsWith('video') ? 'video' : 'image');
          e.target.value = '';
        }} />
      <input ref={docInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt" style={{ display: 'none' }}
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) handleFileUpload(file, 'document');
          e.target.value = '';
        }} />

      {/* Header */}
      <div style={{ background: theme.colors.primary, padding: '14px 16px', color: theme.colors.white, display: 'flex', alignItems: 'center', gap: theme.spacing.md, flexShrink: 0 }}>
        <button onClick={() => navigate('/chat')} style={{ background: 'none', border: 'none', color: theme.colors.white, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div onClick={() => otherPersonId && navigate(`/utente/${otherPersonId}`)}
          style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, cursor: otherPersonId ? 'pointer' : 'default', flex: 1 }}>
          <div style={{ width: 38, height: 38, borderRadius: theme.radius.circle, background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: theme.font.size.lg, overflow: 'hidden', flexShrink: 0 }}>
            {avatarUrl ? <img src={avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : otherPerson?.[0]?.toUpperCase() || '?'}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: theme.font.size.lg, display: 'flex', alignItems: 'center' }}>
              {otherPerson}
              {isVerified && <BadgeVerificato size={14} />}
              {otherPersonId && <span style={{ marginLeft: 4 }}>→</span>}
            </div>
            <div style={{ fontSize: theme.font.size.xs, opacity: 0.8 }}>📋 {jobTitle}</div>
          </div>
        </div>
      </div>

      {/* Cancellazione — richiesta / risposta / esito */}
      {showCancellationCard && (
        <div style={{ padding: '12px 16px 0', flexShrink: 0 }}>
          <CancellationCard
            application={application}
            isApplicant={isApplicant}
            profile={profile}
            conversationId={conversationId}
            session={session}
            otherPersonId={otherPersonId}
            otherPerson={otherPerson}
            onUpdate={(fields) => setApplication(prev => ({ ...prev, ...fields }))}
          />
        </div>
      )}

      {/* Sticky banner — in attesa conferma */}
      {showStickyBannerPending && completionMsg && (
        <div style={{ background: theme.colors.white, borderBottom: `2px solid ${theme.colors.primary}`, flexShrink: 0 }}>
          <div style={{ background: theme.colors.primary, color: theme.colors.white, padding: '8px 14px', fontSize: theme.font.size.base, fontWeight: 700, textAlign: 'center' }}>
            🎉 Accordo raggiunto — {formatOfferTotal(completionMsg.offer_data)}
          </div>
          <CompletionCard application={application} isApplicant={isApplicant} completionMsg={completionMsg} isSticky={true} onScrollToCard={scrollToCompletionCard} onUpdate={(fields) => setApplication(prev => ({ ...prev, ...fields }))} conversationId={conversationId} session={session} otherPersonId={otherPersonId} otherPerson={otherPerson} />
        </div>
      )}

      {/* Sticky banner — già confermato */}
      {showStickyBannerConfirmed && (
        <div style={{ background: theme.colors.primaryLight, borderBottom: '1px solid #a5d6a7', padding: '10px 16px', flexShrink: 0, cursor: 'pointer' }} onClick={scrollToCompletionCard}>
          <p style={{ margin: 0, fontSize: theme.font.size.base, color: theme.colors.primary, fontWeight: 600, textAlign: 'center' }}>
            ✅ Hai confermato il completamento — In attesa dell'altra conferma...{' '}
            <span style={{ fontSize: theme.font.size.xs, opacity: 0.7 }}>(tocca per vedere)</span>
          </p>
        </div>
      )}

      {/* Banner recensione */}
      {showTopReviewBanner && (
        <div style={{ background: theme.colors.warningBgSoft, borderBottom: `1px solid ${theme.colors.warningBorder}`, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing.md, flexShrink: 0 }}>
          <div>
            <p style={{ margin: 0, fontWeight: 700, fontSize: theme.font.size.md, color: theme.colors.warningText }}>⭐ Lavoro completato!</p>
            <p style={{ margin: '2px 0 0', fontSize: theme.font.size.sm, color: theme.colors.textMuted }}>Lascia una recensione a {otherPerson}</p>
          </div>
          <button onClick={() => navigate(`/recensione/${applicationId}`, { state: { otherPerson, jobTitle, avatarUrl } })}
            style={{ background: theme.colors.warningText, color: theme.colors.white, border: 'none', borderRadius: theme.radius.sm, padding: '8px 16px', fontSize: theme.font.size.base, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
            Recensisci
          </button>
        </div>
      )}

      {/* Banner già recensito */}
      {alreadyCompleted && alreadyReviewed && (
        <div style={{ background: theme.colors.primaryLight, borderBottom: '1px solid #a5d6a7', padding: '10px 16px', textAlign: 'center', flexShrink: 0 }}>
          <p style={{ margin: 0, fontSize: theme.font.size.base, color: theme.colors.primary, fontWeight: 600 }}>✅ Hai già lasciato la tua recensione per questo lavoro</p>
        </div>
      )}

      {/* Messaggi */}
      <div style={{ flex: 1, overflowY: 'auto', padding: theme.spacing.lg, background: '#f5f5f5', paddingBottom: 200 }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', marginTop: 40, color: theme.colors.textMuted, fontSize: theme.font.size.md }}>Nessun messaggio ancora. Di' ciao! 👋</div>
        )}

        {Object.entries(groupedMessages).map(([date, msgs]) => (
          <div key={date}>
            <div style={{ textAlign: 'center', margin: '12px 0', fontSize: theme.font.size.sm, color: theme.colors.textMuted }}>
              <span style={{ background: theme.colors.border, padding: '2px 10px', borderRadius: theme.radius.sm }}>{date}</span>
            </div>

            {msgs.map(msg => {
              const isMe = msg.sender_id === session.user.id;
              const isLastCompletion = msg.type === 'completion_request' && msg.id === completionMsgId;

              if (msg.type === 'application') {
                const appData = msg.offer_data || {};
                return (
                  <ApplicationCard key={msg.id}
                    app={{ id: appData.application_id, job_title: appData.job_title, applicant_email: appData.applicant_email, applicant_id: appData.applicant_id, applicant_name: appData.applicant_name, proposed_price: appData.proposed_price, proposed_price_type: appData.proposed_price_type, proposed_hours: appData.proposed_hours, selected_dates: appData.selected_dates, time_slot: appData.job_time_slot, message: appData.message, status: application?.status || 'in attesa' }}
                    session={session} profile={profile} conversationId={conversationId}
                    onStatusChange={(appId, newStatus) => setApplication(prev => ({ ...prev, status: newStatus }))} />
                );
              }

              if (msg.type === 'completion_request') {
                if (!isLastCompletion) return null;
                if (isCancelled) return null;
                if (!alreadyCompleted && hasPendingCancellation) return null;
                return (
                  <div key={msg.id} ref={completionCardRef} style={{ display: 'flex', justifyContent: 'center', marginBottom: theme.spacing.lg }}>
                    <div style={{ width: '90%', borderRadius: theme.radius.lg, border: `2px solid ${theme.colors.primary}`, background: theme.colors.white, overflow: 'hidden', boxShadow: theme.shadow.popup }}>
                      {alreadyCompleted && (
                        <div style={{ background: theme.colors.primary, color: theme.colors.white, padding: '10px 14px', fontSize: theme.font.size.base, fontWeight: 700, textAlign: 'center' }}>
                          🏆 Lavoro completato
                        </div>
                      )}
                      {alreadyCompleted
                        ? <ReceiptCard application={application} completionMsg={completionMsg} profile={profile} session={session} isApplicant={isApplicant} />
                        : <CompletionCard application={application} isApplicant={isApplicant} completionMsg={completionMsg} isSticky={false} onScrollToCard={scrollToCompletionCard} onUpdate={(fields) => setApplication(prev => ({ ...prev, ...fields }))} conversationId={conversationId} session={session} otherPersonId={otherPersonId} otherPerson={otherPerson} />
                      }
                    </div>
                  </div>
                );
              }

              if (msg.type === 'offer') {
                return <OfferCard key={msg.id} msg={msg} isMe={isMe} isApplicant={isApplicant} applicantId={applicantId} employerId={employerId} application={application} session={session} conversationId={conversationId} onUpdate={(fields) => setApplication(prev => ({ ...prev, ...fields }))} formatTime={formatTime} />;
              }

              if (msg.type === 'media') {
                return <MediaMessage key={msg.id} msg={msg} isMe={isMe} formatTime={formatTime} />;
              }

              return (
                <div key={msg.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                  <div style={{ maxWidth: '75%', padding: '10px 14px', borderRadius: isMe ? theme.bubble.mine : theme.bubble.theirs, background: isMe ? theme.colors.primary : theme.colors.white, color: isMe ? theme.colors.white : theme.colors.textPrimary, boxShadow: '0 1px 2px rgba(0,0,0,0.1)', fontSize: theme.font.size.md, lineHeight: 1.4, whiteSpace: 'pre-line' }}>
                    <div>{msg.content}</div>
                    <div style={{ fontSize: 10, opacity: 0.7, marginTop: 4, textAlign: 'right' }}>
                      {formatTime(msg.created_at)}
                      {isMe && <span style={{ marginLeft: 4 }}>{msg.read ? '✓✓' : '✓'}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Overlay per chiudere menu media */}
      {showMediaMenu && (
        <div
          onClick={() => setShowMediaMenu(false)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 150 }}
        />
      )}

      {/* Menu media */}
      {showMediaMenu && (
        <div style={{ position: 'fixed', bottom: 130, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: theme.layout.maxWidth, boxSizing: 'border-box', padding: '0 16px', zIndex: 200 }}>
          <div style={{ background: theme.colors.white, borderRadius: theme.radius.lg, boxShadow: theme.shadow.floating, overflow: 'hidden' }}>
            <button onClick={() => { setShowMediaMenu(false); fileInputRef.current?.click(); }}
              style={{ width: '100%', padding: '14px 16px', border: 'none', background: 'none', textAlign: 'left', fontSize: theme.font.size.lg, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: theme.spacing.md, borderBottom: '1px solid #f0f0f0' }}>
              <span style={{ fontSize: 22 }}>📷</span> Foto o Video
            </button>
            <button onClick={() => { setShowMediaMenu(false); docInputRef.current?.click(); }}
              style={{ width: '100%', padding: '14px 16px', border: 'none', background: 'none', textAlign: 'left', fontSize: theme.font.size.lg, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
              <span style={{ fontSize: 22 }}>📄</span> Documento
            </button>
          </div>
        </div>
      )}

      {/* Area input */}
      <div style={{ position: 'fixed', bottom: 60, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: theme.layout.maxWidth, boxSizing: 'border-box', background: theme.colors.white, borderTop: `1px solid ${theme.colors.border}`, boxShadow: '0 -2px 8px rgba(0,0,0,0.08)', zIndex: 100 }}>
        {showOfferButton && showOfferForm && (
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${theme.colors.border}`, background: '#f0faf0' }}>
            <div style={{ fontSize: theme.font.size.base, fontWeight: 700, color: theme.colors.primary, marginBottom: theme.spacing.sm }}>💼 Invia offerta formale</div>
            <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flex: 1, minWidth: 90 }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: theme.colors.primary, fontWeight: 700 }}>€</span>
                <input type="number" value={offerPrice} onChange={e => setOfferPrice(e.target.value)} placeholder="Il tuo prezzo" autoFocus min="0" step="0.01"
                  style={{ width: '100%', padding: '9px 12px 9px 24px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.primary}`, fontSize: theme.font.size.md, boxSizing: 'border-box' }} />
              </div>
              <select value={offerPriceType} onChange={e => setOfferPriceType(e.target.value)}
                style={{ padding: '9px 10px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.primary}`, fontSize: theme.font.size.base, background: theme.colors.white, color: theme.colors.primary, fontWeight: 600 }}>
                <option value="fisso">fisso</option>
                <option value="ora">/ ora</option>
              </select>
              {offerPriceType === 'ora' && (
                <input type="number" value={offerHours} onChange={e => setOfferHours(e.target.value)}
                  placeholder="Ore" min="0.5" step="0.5"
                  style={{ width: 64, padding: '9px 8px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.primary}`, fontSize: theme.font.size.md, boxSizing: 'border-box' }} />
              )}
              <button onClick={handleSendOffer} disabled={sendingOffer || !offerPrice || (offerPriceType === 'ora' && !offerHours)}
                style={{ background: (offerPrice && (offerPriceType !== 'ora' || offerHours)) ? theme.colors.primary : '#ccc', color: theme.colors.white, border: 'none', borderRadius: theme.radius.sm, padding: '9px 14px', fontSize: theme.font.size.base, fontWeight: 700, cursor: (offerPrice && (offerPriceType !== 'ora' || offerHours)) ? 'pointer' : 'not-allowed' }}>Invia</button>
              <button onClick={() => setShowOfferForm(false)}
                style={{ background: 'none', border: 'none', color: theme.colors.textMuted, fontSize: 16, cursor: 'pointer', padding: '9px 4px' }}>✕</button>
            </div>
            {offerPriceType === 'ora' && offerPrice && offerHours && (
              <div style={{ marginTop: 8, fontSize: theme.font.size.sm, color: theme.colors.primary, fontWeight: 600 }}>
                Totale concordato: €{offerPrice} × {offerHours}h = €{(parseFloat(offerPrice) * parseFloat(offerHours)).toFixed(2)}
              </div>
            )}
          </div>
        )}

        <div style={{ padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
          {showOfferButton && (
            <button onClick={() => setShowOfferForm(!showOfferForm)} style={{ width: '100%', background: showOfferForm ? theme.colors.primary : theme.colors.primaryLight, color: showOfferForm ? theme.colors.white : theme.colors.primary, border: `1px solid ${theme.colors.primary}`, borderRadius: theme.radius.sm, padding: '7px', fontSize: theme.font.size.base, fontWeight: 600, cursor: 'pointer' }}>
              💼 {showOfferForm ? 'Chiudi form offerta' : 'Invia offerta formale'}
            </button>
          )}

          <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'flex-end' }}>

            <button
              onClick={() => { setShowMediaMenu(m => !m); setShowOfferForm(false); }}
              disabled={uploadingMedia || recording}
              style={{ width: 38, height: 38, borderRadius: theme.radius.circle, flexShrink: 0, background: showMediaMenu ? theme.colors.primary : theme.colors.primaryLight, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, color: showMediaMenu ? theme.colors.white : theme.colors.primary }}>
              {uploadingMedia ? '⏳' : '+'}
            </button>

            {recording ? (
              <div style={{ flex: 1, padding: '10px 14px', borderRadius: theme.radius.pill, border: `1px solid ${theme.colors.error}`, background: theme.colors.errorBg, fontSize: theme.font.size.md, color: theme.colors.error, display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                <span style={{ width: 8, height: 8, borderRadius: theme.radius.circle, background: theme.colors.error, display: 'inline-block' }} />
                Registrazione {formatSec(recordingSeconds)}
              </div>
            ) : (
              <textarea value={newMessage} onChange={e => setNewMessage(e.target.value)}
                onKeyDown={handleKeyDown} placeholder="Scrivi un messaggio..." rows={1}
                style={{ flex: 1, padding: '10px 14px', borderRadius: theme.radius.pill, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.md, resize: 'none', outline: 'none', fontFamily: theme.font.family, maxHeight: 100, overflowY: 'auto' }} />
            )}

            {!newMessage.trim() ? (
              <button
                onPointerDown={startRecording}
                onPointerUp={stopRecording}
                onPointerLeave={stopRecording}
                style={{ width: 42, height: 42, borderRadius: theme.radius.circle, flexShrink: 0, background: recording ? theme.colors.error : theme.colors.primary, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: theme.colors.white }}>
                🎤
              </button>
            ) : (
              <button onClick={handleSend} disabled={sending}
                style={{ width: 42, height: 42, borderRadius: theme.radius.circle, flexShrink: 0, background: theme.colors.primary, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: theme.colors.white }}>➤</button>
            )}
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}

export default ChatDetail;